<template lang="pug">

div

    //- Navigation
    navigation

    //- Automatische Synchronisierung (Cron)
    .ui.top.attached.header
        .content Automatische Synchronisierung
    .ui.bottom.attached.segment
        form.ui.form(v-on:submit.prevent='saveSchedule')
            .field
                .ui.checkbox
                    input(
                        type='checkbox'
                        id="syncCronEnabled"
                        v-model='schedule.enabled'
                    )
                    label(for="syncCronEnabled") Synchronisierung im Hintergrund per Cron ausführen
            .field(v-if='schedule.enabled')
                label Cron-Zeitplan
                .fields
                    .field(v-for='field in cronFields' v-bind:key='field.key')
                        label {{ field.label }}
                        input(
                            type='text'
                            v-model='cronParts[field.key]'
                            maxlength='4'
                            style='width: 4.5rem'
                        )
            .ui.info.message(v-if='schedule.enabled')
                i.info.circle.icon
                | "*" = jeder Wert. Beispiel: 0 0 3 * * * = täglich um 03:00 Uhr.
            button.ui.primary.labeled.icon.button(
                type='submit'
                v-bind:class='{ loading: scheduleSaving }'
                v-bind:disabled='scheduleSaving'
            )
                i.save.icon
                | Speichern

    //- MOCO-Synchronisierung
    .ui.top.attached.header
        .content Synchronisierungsoptionen
    .ui.bottom.attached.segment

        .ui.form
            .grouped.fields
                .field
                    .ui.checkbox
                        input(
                            type='checkbox'
                            id="includeContacts"
                            v-model='includeContacts'
                        )
                        label(for="includeContacts") Kontakte synchronisieren
                .field
                    .ui.checkbox
                        input(
                            type='checkbox'
                            id="includeUsers"
                            v-model='includeUsers'
                        )
                        label(for="includeUsers") Benutzer synchronisieren

    .ui.buttons
        button.ui.labeled.icon.button(
            v-bind:class='{ loading: pendingOperation === "dry-run" }'
            v-bind:disabled='pendingOperation !== null'
            v-on:click='runDryRun'
        )
            i.search.icon
            | Dry-Run
        button.ui.labeled.icon.primary.button(
            v-bind:class='{ loading: pendingOperation === "sync" }'
            v-bind:disabled='pendingOperation !== null'
            v-on:click='runSync'
        )
            i.refresh.icon
            | Synchronisieren

    //- Dry-Run-Ergebnis
    template(v-if='dryRunSummary')
        h4.ui.header Dry-Run-Ergebnis

        .ui.error.message(v-if='dryRunSummary.mocoError')
            .header MOCO-API nicht erreichbar
            p {{ dryRunSummary.mocoError }}

        table.ui.celled.compact.table
            thead
                tr
                    th Bereich
                    th Verarbeitet
                    th Bereit
                    th Mit Fehlern
            tbody
                tr
                    td Unternehmen
                    td {{ dryRunSummary.companies.processed }}
                    td {{ dryRunSummary.companies.ready }}
                    td {{ dryRunSummary.companies.withErrors }}
                tr
                    td Kontakte
                    td {{ dryRunSummary.contacts.processed }}
                    td {{ dryRunSummary.contacts.ready }}
                    td {{ dryRunSummary.contacts.withErrors }}
                tr
                    td Benutzer
                    td {{ dryRunSummary.users.processed }}
                    td {{ dryRunSummary.users.ready }}
                    td {{ dryRunSummary.users.withErrors }}

        template(v-if='dryRunSummary.issues.length')
            h5.ui.header Hinweise / Fehler
            table.ui.celled.compact.table
                thead
                    tr
                        th Bereich
                        th Eintrag
                        th Schwere
                        th Code
                        th Meldung
                tbody
                    tr(v-for='(issue, index) in dryRunSummary.issues' v-bind:key='index')
                        td {{ issue.entityType }}
                        td {{ issue.entityName }}
                        td
                            .ui.label(v-bind:class='severityClass(issue.severity)') {{ issue.severity }}
                        td {{ issue.code }}
                        td {{ issue.message }}

    //- Sync-Ergebnis
    template(v-if='syncSummary')
        h4.ui.header Sync-Ergebnis

        .ui.error.message(v-if='syncSummary.error')
            p {{ syncSummary.error }}

        table.ui.celled.compact.table
            thead
                tr
                    th Bereich
                    th Erstellt
                    th Aktualisiert
                    th Übersprungen
                    th Fehler
                    th Gesamt
            tbody
                tr(v-for='row in syncRows' v-bind:key='row.key')
                    td {{ row.label }}
                    td {{ row.created }}
                    td {{ row.updated }}
                    td {{ row.skipped }}
                    td {{ row.failed }}
                    td {{ row.total }}

        .ui.info.message(v-if='!syncSummary.total')
            | Es wurden keine Änderungen vorgenommen.

</template>

<script>
    import store from '@/store'
    import { build_sync_summary, build_dry_run_summary } from '@/utils/moco'
    import Navigation from '@/components/settings/Navigation.vue'

    const CRON_FIELDS = [
        { key: 'second', label: 'Sekunde' },
        { key: 'minute', label: 'Minute' },
        { key: 'hour', label: 'Stunde' },
        { key: 'day', label: 'Tag' },
        { key: 'month', label: 'Monat' },
        { key: 'weekday', label: 'Wochentag' },
    ]

    export default {
        name: 'settings',

        components: { Navigation },

        data() {
            return {
                includeContacts: true,
                includeUsers: true,
                pendingOperation: null,
                schedule: { enabled: false, cronExpression: '0 0 3 * * *' },
                scheduleSaving: false,
                cronParts: { second: '0', minute: '0', hour: '3', day: '*', month: '*', weekday: '*' },
            }
        },

        computed: {

            lastDryRun() {
                return store.getters['settings/lastDryRun']
            },

            lastSyncResult() {
                return store.getters['settings/lastSyncResult']
            },

            dryRunSummary() {
                return this.lastDryRun ? build_dry_run_summary(this.lastDryRun) : null
            },

            syncSummary() {
                return this.lastSyncResult ? build_sync_summary(this.lastSyncResult) : null
            },

            syncRows() {
                const summary = this.syncSummary
                if (!summary) return []
                return [
                    { key: 'companies', label: 'Unternehmen', ...summary.companies },
                    { key: 'contacts', label: 'Kontakte', ...summary.contacts },
                    { key: 'users', label: 'Benutzer', ...summary.users },
                ]
            },

            cronFields() {
                return CRON_FIELDS
            },

            cronExpression() {
                return CRON_FIELDS.map(field => this.cronParts[field.key]).join(' ')
            },

        },

        created() {
            this.loadSchedule()
        },

        methods: {

            async loadSchedule() {
                try {
                    const schedule = await store.dispatch('settings/loadMocoSyncSchedule')
                    this.schedule = { ...schedule }
                    this.syncCronParts(this.schedule.cronExpression)
                } catch (e) {
                    this.$growl.error('Cron-Einstellungen konnten nicht geladen werden')
                }
            },

            syncCronParts(expression) {
                const parts = (expression || '0 0 3 * * *').split(' ')
                CRON_FIELDS.forEach((field, index) => {
                    this.cronParts[field.key] = parts[index] || '*'
                })
            },

            async saveSchedule() {
                this.scheduleSaving = true
                try {
                    this.schedule.cronExpression = this.cronExpression
                    const schedule = await store.dispatch('settings/saveMocoSyncSchedule', this.schedule)
                    this.schedule = { ...schedule }
                    this.$growl.success('Cron-Einstellungen wurden gespeichert')
                } catch (e) {
                    this.$growl.error('Cron-Einstellungen konnten nicht gespeichert werden')
                } finally {
                    this.scheduleSaving = false
                }
            },

            severityClass(severity) {
                return {
                    INFO: 'blue',
                    WARNING: 'yellow',
                    ERROR: 'red',
                }[severity] || 'grey'
            },

            async runDryRun() {
                this.pendingOperation = 'dry-run'
                try {
                    await store.dispatch('settings/mocoDryRun', {
                        includeContacts: this.includeContacts,
                        includeUsers: this.includeUsers,
                    })
                    this.$growl.success('Dry-Run abgeschlossen')
                } catch (e) {
                    this.$growl.error('Dry-Run fehlgeschlagen')
                } finally {
                    this.pendingOperation = null
                }
            },

            async runSync() {
                const confirmed = await this.$growl.confirm('Möchten Sie die MOCO-Synchronisation wirklich starten?')
                if (!confirmed) return

                this.pendingOperation = 'sync'
                try {
                    await store.dispatch('settings/mocoSyncRun', {
                        includeContacts: this.includeContacts,
                        includeUsers: this.includeUsers,
                    })
                    this.$growl.success('Synchronisation abgeschlossen')
                } catch (e) {
                    this.$growl.error('Synchronisation fehlgeschlagen')
                } finally {
                    this.pendingOperation = null
                }
            },

        },

    }

</script>
